import { Component, Mixins, Prop, Vue, Watch } from 'vue-property-decorator'
import sumBy from 'lodash/sumBy'
import { AxiosResponse } from 'axios'
import { mapGetters } from 'vuex'
import { DateMixin, emitter, NumberMixin, Spinner } from '@kwixeo/interface-vue'
import { KForm, KFormDatepicker, KFormEditor, KFormSpinner, KFormInput, KFormDatetime, KFormCheckbox } from '@kwixeo/form-vue'
import { BFormRow, BCol, BFormGroup, BFormRadioGroup, BFormRadio, BAlert, BButton, VBTooltip } from 'bootstrap-vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { fetchManualMovement, needFetchManualMovement } from '../../fetchDatas/manualMovement'
import CatalogSearch from '../CatalogSearch/CatalogSearch.vue'
import ProductVariationSelect from '../ProductVariationSelect/ProductVariationSelect.vue'
import LotSelected from '../LotSelected/LotSelected.vue'
import StockLocationSelect from '../StockLocationSelect/StockLocationSelect.vue'
import ProductStockIndicator from '../ProductStockIndicator/ProductStockIndicator.vue'


@Component({
  components: {
    Spinner,
    ProductVariationSelect, LotSelected, CatalogSearch, StockLocationSelect, ProductStockIndicator,
    KForm, KFormDatepicker, KFormEditor, KFormSpinner, KFormInput, KFormDatetime, KFormCheckbox,
    BFormRow, BCol, BFormGroup, BFormRadioGroup, BFormRadio, BAlert, BButton,
    FontAwesomeIcon
  },
  computed: {
    ...mapGetters('subscriptions', ['hasModule'])
  },
  directives: {
    'b-tooltip': VBTooltip
  }
})
export default class ManualMovementForm extends Mixins(DateMixin, NumberMixin) {
  hasModule!: (key: string) => boolean

  @Prop({ default: null }) value!: KwixeoCatalog.StockManualMovement|null

  private loading = false
  private productWithLotDeleted = false
  private movement: KwixeoCatalog.FormStockManualMovement = {
    contents: []
  }

  get formUrl (): string {
    return '/catalog/stock/manual' + (this.movement.id ? '/' + this.movement.id : '')
  }

  get formMethod (): string {
    return this.movement.id ? 'PUT' : 'POST'
  }

  get canGenerateSerial (): (content: KwixeoCatalog.FormStockManualMovementContent) => boolean {
    return (content: KwixeoCatalog.FormStockManualMovementContent) => {
      const lots = content.lots && content.lots.filter(lot => lot.id === undefined)
      const serial = lots && lots.length > 0 ? lots[0].reference : null
      if (!serial) {
        return true
      }
      const regex = /[0-9]+$/
      const test = regex.exec(serial)
      return test !== null
    }
  }

  get minQuantity (): (content: KwixeoCatalog.FormStockManualMovementContent) => number {
    return content => sumBy(content.createdLots, 'quantity') ?? 0
  }

  get stockLocationLots (): KwixeoCatalog.StockLocation|null|undefined {
    return this.movement.type === 3 ? this.movement.from_stock_location : this.movement.stock_location
  }

  get catalogSearchFilters (): KwixeoInterface.GenericObject {
    const filters: KwixeoInterface.GenericObject = {
      with_stock: true
    }

    if (this.movement.type === 3) {
      filters.with_stock_tracking_mode = 0
    }

    return filters
  }

  private selectStartLocation (): void {
    if (this.movement.stock_location?.id === this.movement.from_stock_location?.id) {
      this.movement.stock_location = null
    }
  }

  private selectEndLocation (): void {
    if (this.movement.from_stock_location?.id === this.movement.stock_location?.id) {
      this.movement.from_stock_location = null
    }
  }

  private resetUsedLots (): void {
    this.movement.contents.forEach((content, i) => {
      Vue.set(this.movement.contents[i], 'used_lots', [])
    })
  }

  @Watch('movement.type')
  private watchMovementType (): void {
    if (this.movement.type !== 3) {
      return
    }

    this.movement.contents = this.movement.contents.filter(content => {
      return content.product.stock_tracking_mode === 0
    })
  }

  @Watch('value', { immediate: true })
  private async watchValue (value: KwixeoCatalog.StockManualMovement|null): Promise<void> {
    if (value) {
      if (value.id && needFetchManualMovement(value)) {
        value = await fetchManualMovement(value.id)
      }
      const movement: KwixeoCatalog.FormStockManualMovement = JSON.parse(JSON.stringify(value))
      movement.contents.map(content => {
        if (content.lots && content.lots.length > 0) {
          content.createdLots = content.lots
          content.lots = []
          if (!content.used_lots) {
            content.used_lots = []
          }
        }
        return content
      })
      this.movement = movement
    } else {
      this.movement = {
        type: 1,
        date: new Date(),
        contents: []
      }
    }
  }

  private addProduct (data: KwixeoCatalog.SearchResultData): void {
    const product = data.data as KwixeoCatalog.Product

    if (this.movement.type === 3 && product.stock_tracking_mode !== 0) {
      return
    }

    const searchFunc = (content: KwixeoCatalog.FormStockManualMovementContent) => content.product_id === product.id && content.product.variations_length === 0
    const line = this.movement.contents.find(searchFunc) as KwixeoCatalog.StockManualMovementContent
    if (line) {
      line.quantity++

      const product = line.product as KwixeoCatalog.Product
      if (product.with_stock && product.stock_tracking_mode && product.stock_tracking_mode > 0) {
        const index = this.movement.contents.findIndex(searchFunc)
        this.updateQuantity(index)
      }
      return
    }

    const index = this.movement.contents.push({
      product: product,
      product_id: product.id,
      variation: null,
      quantity: 1,
      lots: [],
      generate_serial: true
    })
    this.updateQuantity(index - 1)
  }

  private deleteLine (i: number): void {
    const content = this.movement.contents[i]
    if (content.createdLots && content.createdLots.length > 0) {
      this.productWithLotDeleted = true
    }

    Vue.delete(this.movement.contents, i)
  }

  private getInputName (index: number, name: string, lot: number|null = null): string {
    if (name.indexOf('.')) {
      name = name.replaceAll('.', '][')
    }

    let input = 'contents[' + index + ']'
    if (lot !== null) {
      input += '[lots][' + lot + ']'
    }
    input += '[' + name + ']'

    return input
  }

  private updateQuantity (index: number): void {
    const content = this.movement.contents[index]
    const stockMode = Number(content.product?.stock_tracking_mode)
    if (stockMode === 0) {
      return
    }

    const lots = content.lots as Partial<KwixeoCatalog.ProductLot>[]
    const qty = this.roundNumber(content.quantity - this.roundNumber(sumBy(content.createdLots, 'quantity') ?? 0, 5), 5)
    if (stockMode === 2) {
      // If stock with serial
      if (qty > lots.length) {
        for (let i = lots.length; i < qty; i++) {
          lots.push({
            reference: null
          })
        }
      } else if (lots.length > qty) {
        for (let i = lots.length; i > qty; i--) {
          lots.splice(-1, 1)
        }
      }
    } else if (stockMode === 1) {
      // If stock with lot
      if (lots.length === 0) {
        // If have not some lots
        if (qty > 0) {
          lots.push({
            reference: null,
            expire_at: null,
            quantity: qty
          })
        }
      } else {
        // If have already some lots
        const actualQty = sumBy(lots, 'quantity')
        if (actualQty < qty) {
          // If need to add quantity, add to the last lot
          const lastLot = lots[lots.length - 1]
          lastLot.quantity = Number(lastLot.quantity) + (qty - actualQty)
        } else if (actualQty > qty) {
          // If have too many quantity
          const lastLot = lots[lots.length - 1]
          const newQty = Number(lastLot.quantity) - (actualQty - qty)
          if (newQty > 0) {
            lastLot.quantity = newQty
          } else {
            lots.splice(-1, 1)
            if (newQty !== 0) {
              this.updateQuantity(index)
            }
          }
        }
      }
    }
  }

  private updateLotQuantity (content: KwixeoCatalog.FormStockManualMovementContent, index: number): void {
    const lots = content.lots as Partial<KwixeoCatalog.ProductLot>[]
    const createdQty = (sumBy(content.createdLots, 'quantity') ?? 0)
    const qty = content.quantity - createdQty
    const actualQty = sumBy(lots, 'quantity')

    if (qty > actualQty) {
      if (index === (lots.length - 1)) {
        // If remove quantity from the last lot, add a new
        lots.push({
          reference: null,
          expire_at: null,
          quantity: qty - actualQty
        })
      } else {
        // Add quantity to the last
        const lastLot = lots[lots.length - 1]
        lastLot.quantity = Number(lastLot.quantity) + (qty - actualQty)
      }
    } else if (actualQty > qty) {
      if (index === (lots.length - 1)) {
        content.quantity = actualQty + createdQty
      } else {
        const lastLot = lots[lots.length - 1]
        const newQty = Number(lastLot.quantity) - (actualQty - qty)
        if (newQty > 0) {
          lastLot.quantity = newQty
        } else {
          lots.splice(-1, 1)
          if (newQty !== 0) {
            this.updateLotQuantity(content, lots.length - 1)
          }
        }
      }
    }
  }

  private generateSerial (content: KwixeoCatalog.FormStockManualMovementContent): void {
    const lots = content.lots?.filter(lot => lot.id === undefined) as Partial<KwixeoCatalog.ProductLot>[]
    if (lots && lots.length > 0 && lots[0].reference) {
      let prefix = ''
      let number = 0
      lots.forEach((lot, i) => {
        if (i === 0) {
          // Take first serial and store datas
          const reference = String(lot.reference)
          const regex = /[0-9]+$/
          const test = regex.exec(reference)
          if (test) {
            prefix = reference.substr(0, test.index)
            number = Number(test[0])
          }
        } else {
          number++ // Increment serial
          lot.reference = prefix + number
        }
      })
    }
  }

  private submitComplete (response: AxiosResponse): void {
    const movement: KwixeoCatalog.StockManualMovement = response.data
    if (this.value && this.value.id) {
      emitter.emit('stockManuaMovementUpdated', { old: this.value, new: movement })
    } else {
      emitter.emit('stockManuaMovementCreated', movement)
    }
  }
}
